import { Label, defineLabels } from './types';

/**
 * A THIRD KEYSPACE: keyed by a synthetic slug, for paginated-table columns that
 * are not a single database field.
 *
 * An earlier draft of §10.5 said composite columns keep their own literal string
 * and that inventing a key for them was how a useful dictionary turns into a
 * useless one. Reversed after counting the actual headers: "Editar" and
 * "Borrar" appear on all twelve tables, and "Actualizado por" / "Actualizada
 * por" had *already* drifted into two spellings. Non-field columns repeat, so
 * they drift like any other repeated string, and nothing stopped a thirteenth
 * table from saying "Modificar".
 *
 * Only the table surface reads this — the audit shows a composite's constituent
 * fields as separate rows, and the forms have no equivalent cell — so only
 * `short` matters and `long` doubles as it.
 *
 * WHAT DOES NOT BELONG HERE: a column rendered from one field through a join.
 * "Actualizado por" is `updated_by_id`, a real column with a real label, so its
 * gender disagreement is fixed in `FIELD_LABELS`, not here. Misfiling it would
 * move a schema-backed key into the one keyspace `check-activity-labels.cjs`
 * cannot validate.
 */
export const COLUMN_LABELS = defineLabels({
    // ── Row actions. On every table, which is why they drift. ────────────────
    edit: { long: 'Editar' },
    delete: { long: 'Borrar' },
    view: { long: 'Ver' },
    accept: { long: 'Aceptar' },
    change_status: { long: 'Cambiar status' },
    edit_optional_details: { long: 'Editar datos opcionales' },
    print_pdf: { long: 'Imprimir pdf' },
    link_product: { long: 'Vincular producto' },
    diagnostics: { long: 'Diagnóstico' },

    // ── Composites: one cell built from two or more fields. ──────────────────
    /** Transfers: the from → to account pair rendered as one cell. */
    transfer_accounts: { long: 'Cuenta' },
    /** Transfers: every receipt's expense or sale folio, joined. */
    transfer_folios: { long: 'Folios' },
    /** Order sales: `order_code` or `invoice_code`, whichever applies. */
    sale_folio: { long: 'Folio' },
    /** Order sales: `reconciliation_only` rendered as a word, not a checkbox. */
    reconciliation: { long: 'Conciliación' },
    /** Accounts: derived from `is_client` and `is_supplier` together. */
    account_type: { long: 'Tipo' },
    /** Order productions: the child rows listed in one cell. */
    products_list: { long: 'Productos' },
    machines_list: { long: 'Máquinas' },
    employees_list: { long: 'Empleados' },
    /** Order productions: computed, backed by no column at all. */
    performance: { long: 'Rendimiento' },
    /** Order sales: the summary total of the row's products. */
    row_total: { long: 'Total' },

    // ── The activities table itself. ─────────────────────────────────────────
    // Activities are never snapshotted — you do not audit the audit — so these
    // can never appear in a diff. They live here rather than in FIELD_LABELS
    // because `title` and `type` are names generic enough that adding them to
    // the shared map would hand them to every other table with a column so
    // named.
    /** Entity plus what changed, composed into one cell. */
    activity: { long: 'Actividad' },
    activity_title: { long: 'Título' },
    activity_type: { long: 'Tipo de actividad' },
});

export type ColumnLabelKey = keyof typeof COLUMN_LABELS;

/** Dynamic lookup — see the note on `lookupFieldLabel`. */
export function lookupColumnLabel(column: string): Label | undefined {
    return (COLUMN_LABELS as Record<string, Label | undefined>)[column];
}
